/* ROWIE — size chart
   ------------------------------------------------------------------
   Two jobs: the CM/IN toggle, and the progress rail over the table's
   native horizontal scroller.

   Cells that can convert carry data-cm with the entered centimetre
   value. Nothing is stored in inches anywhere — the conversion happens
   here so the admin only ever enters one set of numbers.

   Handles three kinds of cell value:
     "72"      → single measurement
     "80-95"   → a range, both ends converted
     "6-12"    → not a measurement; left alone unless marked convertible

   Column headings in the conversion chart carry data-unit-head with
   the original text, so "Bust (cm)" becomes "Bust (in)" on toggle.
*/
(function () {
  var CM_PER_INCH = 2.54;

  function toInches(cm) {
    // One decimal place. Sir store inches natively and show 27.2; we
    // store centimetres, so this is the same precision from the other
    // direction.
    return (cm / CM_PER_INCH).toFixed(1);
  }

  function convertValue(raw, unit) {
    var text = String(raw).trim();

    if (unit === 'cm') {
      return text;
    }

    // Range — convert each end, keep the separator.
    var range = text.match(/^(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)$/);
    if (range) {
      return toInches(parseFloat(range[1])) + '-' + toInches(parseFloat(range[2]));
    }

    // Single number, possibly with a stray unit suffix from migrated
    // data. Anything else is passed through untouched.
    var single = text.match(/^(-?\d+(?:\.\d+)?)\s*(?:cm)?$/i);
    if (single) {
      return toInches(parseFloat(single[1]));
    }

    return text;
  }

  function setUnit(root, unit) {
    var cells = root.querySelectorAll('[data-cm]');
    var i;

    for (i = 0; i < cells.length; i++) {
      cells[i].textContent = convertValue(cells[i].getAttribute('data-cm'), unit);
    }

    var heads = root.querySelectorAll('[data-unit-head]');
    for (i = 0; i < heads.length; i++) {
      var base = heads[i].getAttribute('data-unit-head');
      heads[i].textContent = unit === 'cm'
        ? base
        : base.replace(/\bcm\b/gi, 'in');
    }

    var buttons = root.querySelectorAll('[data-unit]');
    for (i = 0; i < buttons.length; i++) {
      var isActive = buttons[i].getAttribute('data-unit') === unit;
      buttons[i].classList.toggle('is-active', isActive);
      buttons[i].setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    root.setAttribute('data-current-unit', unit);

    // Switching units changes column widths, so the rail is remeasured
    // — but guarded, because the rail is decoration and must never be
    // able to break the conversion itself.
    try {
      updateRail(root);
    } catch (err) {
      console.warn('Size chart rail could not be measured:', err);
    }
  }

  /**
   * Sizes and positions the rail thumb, and hides the rail entirely
   * when there is nothing to scroll.
   *
   * Returns early when clientWidth is 0. That happens whenever the
   * chart sits in a hidden tab panel — measuring then would compute a
   * nonsense ratio and permanently hide the rail, so callers re-run
   * this once the panel becomes visible (see refresh below).
   */
  function updateRail(root) {
    // Explicit attribute, not [role="group"] — the unit toggle wrapper
    // carries that role too, so the selector depended on DOM order.
    var scroller = root.querySelector('[data-sizechart-scroll]');
    var rail = root.querySelector('[data-sizechart-rail]');
    var thumb = root.querySelector('[data-sizechart-thumb]');

    if (!scroller || !rail || !thumb) return;
    if (scroller.clientWidth === 0) return;

    var overflow = scroller.scrollWidth - scroller.clientWidth;

    if (overflow <= 1) {
      rail.setAttribute('hidden', '');
      return;
    }

    rail.removeAttribute('hidden');

    var ratio = scroller.clientWidth / scroller.scrollWidth;
    var thumbPercent = Math.max(ratio * 100, 8);
    thumb.style.width = thumbPercent + '%';

    var progress = scroller.scrollLeft / overflow;
    var travel = 100 - thumbPercent;

    // Split out rather than inlined. The transform percentage is
    // relative to the thumb's own width, not the rail's, so the rail
    // percentage has to be rescaled by the thumb width — inlining it
    // is what hid an unbalanced paren here.
    var offset = (progress * travel / thumbPercent) * 100;
    thumb.style.transform = 'translateX(' + offset + '%)';
  }

  function init(root) {
    if (root.hasAttribute('data-sizechart-ready')) {
      return;
    }
    root.setAttribute('data-sizechart-ready', '');

    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-unit]');
      if (!button || !root.contains(button)) {
        return;
      }
      setUnit(root, button.getAttribute('data-unit'));
    });

    var scroller = root.querySelector('[data-sizechart-scroll]');
    if (scroller) {
      scroller.addEventListener('scroll', function () {
        updateRail(root);
      }, { passive: true });
    }

    window.addEventListener('resize', function () {
      updateRail(root);
    });

    setUnit(root, root.getAttribute('data-base-unit') || 'cm');
  }

  function initAll(scope) {
    var charts = (scope || document).querySelectorAll('[data-size-chart]');
    for (var i = 0; i < charts.length; i++) {
      init(charts[i]);
    }
  }

  /**
   * Remeasures every chart in scope. Called by the size guide drawer
   * when it opens and when a tab is selected, because a chart in a
   * hidden panel cannot be measured.
   */
  function refresh(scope) {
    var charts = (scope || document).querySelectorAll('[data-size-chart]');
    for (var i = 0; i < charts.length; i++) {
      updateRail(charts[i]);
    }
  }

  // Exposed so the size guide drawer can initialise or remeasure a
  // chart it renders or reveals after load.
  window.RowieSizeChart = { init: initAll, setUnit: setUnit, refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
